import { ActionButton, Badge, Card } from "@jhool-io/fe-components";
import { useClientDataImportDiagnosis } from "../../hooks/clients.mutations";
import useToast from "../../../../hooks/useToast";
import Loader from "../../../../shared-ui/Loader/Loader";
import ListState from "../../../../components/ListState/ListState";
import {
    ImportCheckStatus,
    ImportOverallStatus,
} from "../../types/clients.types";

interface ClientIntakeImportDiagnosisInfoProps {
    clientId: string;
}

export default function ClientIntakeImportDiagnosisInfo({
    clientId,
}: ClientIntakeImportDiagnosisInfoProps) {
    const { mutate, isLoading, error, data, isSuccess } =
        useClientDataImportDiagnosis();

    const { toast } = useToast();

    const handleGetDiagnosis = () => {
        mutate(
            { client_ids: [clientId] },
            {
                onSuccess: (res) => {
                    toast({
                        mode: "success",
                        message:
                            res.message ||
                            "Intake import Diagnosis feteched successfully",
                    });
                },
                onError: (err) => {
                    toast({
                        mode: "error",
                        message:
                            err?.response?.data?.message ||
                            "Error fetching intake import diagnosis",
                    });
                },
            }
        );
    };

    const getOverallStatusBadge = (status: ImportOverallStatus) => {
        switch (status) {
            case ImportOverallStatus.SUCCESS:
                return <Badge type="success" content={status} />;
            case ImportOverallStatus.WARNING:
                return <Badge type="warning" content={status} />;

            default:
                return <Badge type="danger" content={status} />;
        }
    };

    const getCheckStatusBadge = (status: ImportCheckStatus) => {
        switch (status) {
            case ImportCheckStatus.PASSED:
                return <Badge type="success" content={status} />;
            case ImportCheckStatus.WARNING:
                return <Badge type="warning" content={status} />;
            case ImportCheckStatus.SKIPPED:
                return <Badge type="info" content={status} />;

            default:
                return <Badge type="danger" content={status} />;
        }
    };

    return (
        <Card>
            <div className="flex items-center justify-between gap-[--spacing-items] flex-wrap">
                <h2 className="text-title font-medium">
                    Client Intake Import Diagnosis Information
                </h2>
                <ActionButton
                    actionTitle={
                        clientId
                            ? "Get results"
                            : "Client was not addded from intake"
                    }
                    onClick={handleGetDiagnosis}
                    disabled={isLoading || !clientId}
                >
                    Get Diagnosis
                </ActionButton>
            </div>
            <div>
                {isLoading ? (
                    <div className="mb-[--spacing-items]">
                        <Loader />
                    </div>
                ) : null}
                {error ? (
                    <div className="mb-[--spacing-items]">
                        <ListState
                            isError
                            stateHelperText="Try reloading this page to solve this issue"
                            errorMsg={
                                error?.response?.data.message ||
                                `Cannot display clients list at this time please try again later`
                            }
                            context="general"
                        />
                    </div>
                ) : null}
                {isSuccess ? (
                    <div className="mb-[--spacing-items]">
                        {data.data.results.map((result) => (
                            <div
                                key={result.client_id}
                                className="flex flex-col gap-y-[--spacing-items]"
                            >
                                <div className="flex flex-col gap-2">
                                    <span className="font-medium">Summary</span>
                                    <span className="flex items-center gap-[--spacing-items] flex-wrap">
                                        {getOverallStatusBadge(
                                            result.overall_status
                                        )}
                                        {result.summary}
                                    </span>
                                </div>

                                {result.checks && result.checks.length > 0 ? (
                                    <div className="grid grid-cols-[repeat(auto-fit,minmax(300px,1fr))] gap-[--spacing-items]">
                                        {result.checks.map((check) => (
                                            <Card
                                                key={check.step}
                                                classNames="bg-[--bg-sidebar-dropdown-open] text-caption flex flex-col gap-y-1"
                                            >
                                                <div className="flex gap-1 flex-wrap items-center">
                                                    <span className="font-medium text-textSec">
                                                        Step:
                                                    </span>
                                                    <span>{check.step}</span>
                                                </div>
                                                <div className="flex gap-1 flex-wrap items-center">
                                                    <span className="font-medium text-textSec">
                                                        Status:
                                                    </span>
                                                    <span>
                                                        {getCheckStatusBadge(
                                                            check.status
                                                        )}
                                                    </span>
                                                </div>
                                                <div className="flex gap-1 flex-wrap items-center">
                                                    <span className="font-medium text-textSec">
                                                        Message:
                                                    </span>
                                                    <span>{check.message}</span>
                                                </div>
                                                {check.suggested_fix ? (
                                                    <div className="flex gap-1 items-center flex-wrap">
                                                        <span className="font-medium text-textSec">
                                                            Suggested fix:
                                                        </span>
                                                        <span>
                                                            {
                                                                check.suggested_fix
                                                            }
                                                        </span>
                                                    </div>
                                                ) : null}
                                            </Card>
                                        ))}
                                    </div>
                                ) : null}
                            </div>
                        ))}
                    </div>
                ) : null}
            </div>
        </Card>
    );
}
